import React from 'react';
import {createStyles, makeStyles} from '@material-ui/styles';
import {Icon} from '@iconify/react';
import clsx from 'clsx';

import {Tooltip} from 'components/Widgets';

import {black80, bold, sizeCaption, sizeLarge, sizeBody1} from 'consts/brand.integration';
import {numberWithCommas} from 'consts/utilities';
import {PlatformsConfig} from 'consts/platforms';
import TalentTierChip from '../TalentTierChip';

interface DataItemProps {
  followers?: number;
  talentTier?: string;
  account?: {followers: number};
  engagementRate?: string;
  tooltip?: string;
  color?: string;
  className?: string;
  icon?: string;
  size?: 'small' | 'large';
  showTalentTier?: boolean;
}

interface StyleProps {
  color?: string;
  size?: 'small' | 'large';
}

const DataItem: React.FC<DataItemProps> = ({
  followers,
  talentTier,
  engagementRate,
  tooltip,
  icon,
  color,
  className,
  size,
  showTalentTier,
}) => {
  const classes = useStyles({color, size});
  if (followers === undefined && !engagementRate) {
    return null;
  }
  const value = followers !== undefined ? numberWithCommas(followers) : engagementRate;
  return (
    <div className={clsx(classes.root, className)}>
      <Tooltip
        overlay={
          <span>
            {tooltip} | {talentTier ? talentTier : '(no talent tier)'}
          </span>
        }
        mouseEnterDelay={0.3}
        placement="bottom"
      >
        {value && <Icon icon={icon} />}
        <span>{value}</span>
        {showTalentTier && (
          <span style={{marginLeft: '1rem'}}>
            <TalentTierChip tier={talentTier ?? ''} size="small" />
          </span>
        )}
      </Tooltip>
    </div>
  );
};

type FollowerDataProps = {
  instagramFollowers?: number;
  instagramTalentTier?: string;
  tiktokFollowers?: number;
  tiktokTalentTier?: string;
  youtubeSubscriberCount?: number;
  youtubeTalentTier?: string;
  twitchFollowers?: number;
  twitchTalentTier?: string;
  engagementRate?: string;
  color?: string;
  className?: string;
  size?: 'small' | 'large';
  showTalentTier?: boolean;
};

const FollowerData: React.FC<FollowerDataProps> = ({
  instagramFollowers,
  instagramTalentTier,
  tiktokFollowers,
  tiktokTalentTier,
  youtubeSubscriberCount,
  youtubeTalentTier,
  twitchFollowers,
  twitchTalentTier,
  engagementRate,
  color,
  className,
  size = 'small',
  showTalentTier,
}) => {
  const dataItems = [
    {
      followers: instagramFollowers,
      icon: PlatformsConfig.instagram.iconMono,
      tooltip: 'Instagram Followers',
      talentTier: instagramTalentTier,
    },
    {
      followers: tiktokFollowers,
      icon: PlatformsConfig.tiktok.iconMono,
      tooltip: 'TikTok Followers',
      talentTier: tiktokTalentTier,
    },
    {
      followers: youtubeSubscriberCount,
      icon: PlatformsConfig.youtube.iconMono,
      tooltip: 'YouTube Subscribers',
      talentTier: youtubeTalentTier,
    },
    {
      followers: twitchFollowers,
      icon: PlatformsConfig.twitch.iconMono,
      tooltip: 'Twitch Followers',
      talentTier: twitchTalentTier,
    },
    {
      engagementRate: engagementRate,
      icon: 'material-symbols:show-chart-rounded',
      tooltip: 'Engagement Rate',
    },
  ];
  const classes = useStyles();
  return (
    <div className={clsx(classes.root, className)}>
      {dataItems.map((item, index) => (
        <DataItem
          key={index}
          followers={item.followers && item.followers > 0 ? item.followers : undefined}
          talentTier={item.talentTier && item.talentTier !== '' ? item.talentTier : undefined}
          engagementRate={item.engagementRate}
          icon={item.icon}
          tooltip={item.tooltip}
          size={size}
          color={color}
          showTalentTier={showTalentTier}
        />
      ))}
    </div>
  );
};

const useStyles = makeStyles<StyleProps>(() =>
  createStyles({
    root: (props: StyleProps) => ({
      display: 'flex',
      alignItems: 'center',
      color: props.color || black80,
      fontWeight: bold,
      fontSize: props.size === 'large' ? sizeBody1 : sizeCaption,
      marginRight: props.size === 'large' ? '10px' : '5px',
      '& span': {
        display: 'flex',
        alignItems: 'center',
      },
      '& svg': {
        height: props.size === 'large' ? '24px' : sizeLarge,
        width: props.size === 'large' ? '24px' : sizeLarge,
        color: props.color || black80,
        marginRight: '3px',
      },
      '&:not(:first-child)': {
        borderLeft: `1px solid ${black80}`,
        paddingLeft: props.size === 'large' ? '10px' : '5px',
        maxHeight: '15px',
      },
    }),
  })
);

export default FollowerData;
